import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppStartupService } from '../shared/app-startup.service';
import { Page } from '../shared/page';
import { CustomizationService } from './customization/services/customization.service';

@Component({
  selector: 'app-chat-inmessage',
  templateUrl: './chat-inmessage.component.html',
  styleUrls: ['./chat-inmessage.component.scss', './mbr-v2.css'],
  encapsulation: ViewEncapsulation.None,
})
export class ChatInmessageComponent implements OnInit {
  legacy: boolean;
  // Will be set to false after chat window has finished loading
  isLoading = true;
  standalone: boolean;

  constructor(
    public customizationService: CustomizationService,
    private appStartupService: AppStartupService,
    private translateService: TranslateService,
    private http: HttpClient,
    router: Router,
    @Inject(DOCUMENT) private document: any
  ) {
    const urlParams = new URLSearchParams(window.location.search);
    const version = urlParams.get('version');
    const standalone = urlParams.get('standalone');
    const customizationLanguage = urlParams.get('customizationLanguage');
    const extrasParams = urlParams.get('extras');
    const extras = (extrasParams || '').split(',');

    this.legacy = !version || `${version}` !== `2`;
    this.customizationService.customization =
      !this.legacy && (!standalone || standalone === 'false') && extras.some(extra => extra === 'customization');
    this.customizationService.customizationDisplayed = !extras.some(extra => extra === 'collapsed');
    if (this.customizationService.customization) {
      this.customizationService.customizationLanguage = customizationLanguage as any;
      this.customizationService.advancedCustomization = extras.some(extra => extra === 'advanced');
      router.navigate(['customization'], { queryParamsHandling: 'preserve' });
    }

    this.translateService.setDefaultLang('en');

    if (!this.legacy) {
      window.addEventListener(
        'message',
        event => {
          if (event.data.type === 'customTranslations') {
            for (const [key, value] of Object.entries(event.data.translations)) {
              this.translateService.set(key, value as string, this.translateService.currentLang);
            }
          }
        },
        false
      );
    }
  }

  /**
   * we add a CSS class in case of standalone url parameter
   */
  ngOnInit(): void {
    this.standalone = this.appStartupService.isStandaloneMode();
    if (this.standalone) {
      this.document.documentElement.classList.add('standalone');
    }
    this.appStartupService.pageInfo$.subscribe(pageInfo => this.processPage(pageInfo));
  }

  /**
   * Inject chat window css, translate chat header, configure langage and request custom trads from MBR.
   * @param pageInfo The page object emitted by the chat component
   */
  private processPage(pageInfo: Page) {
    if (pageInfo.icon_url) {
      // legacy avatar; should instead be set in look-and-feel css
      this.injectCSS(`.alc-from-brand .alc-message-avatar { background-image: url(${pageInfo.icon_url}) }`);
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (this.legacy) {
      const lookAndFeel = urlParams.get('look-and-feel');
      if (pageInfo.chat_window_css) {
        // legacy mode (CoopVoce and Alcmeon): the chat window's CSS is in the database
        this.injectCSS(pageInfo.chat_window_css);
      } else if (lookAndFeel) {
        this.injectLocalCSS(lookAndFeel);
      }
    } else if (pageInfo.override_default_chat_window_css) {
      this.injectCSS(pageInfo.override_default_chat_window_css);
    }

    this.initLang(urlParams.get('lang'), pageInfo);
    this.setTranslations();

    this.isLoading = false;
  }

  // configure langage and request custom trads from MBR.
  // Use lang code parameter in mbr config if specified, else use lang code returned by /self
  private initLang(langFromUrl, pageInfo) {
    const lang = langFromUrl ? langFromUrl : pageInfo.lang_code;
    this.translateService.use(lang);
    if (this.legacy) {
      window.parent.postMessage({ type: 'sendCustomTranslations' }, '*');
    } else {
      this.appStartupService.getLabels(lang);
      const isMobile = (): boolean => {
        const { userAgent } = window.navigator;
        const userAgentName = userAgent.toLowerCase();
        return (
          // userAgentData is experimental and not available on all browser. BTW it is a good way to know if we are on mobile.
          window.navigator['userAgentData']?.mobile ||
          /iphone|ipod|ipad|android|blackberry|windows phone|nokia|webos|mobile/.test(userAgentName)
        );
      };
      if (pageInfo?.enable_move_chat && !this.customizationService.customization && !this.standalone && !isMobile()) {
        window.parent.postMessage({ type: 'injectDragDropScript', position: pageInfo.position }, '*');
      }
    }
  }

  private setTranslations() {
    document.querySelector('title').innerText = this.translateService.instant('html_title');
    const button: any = document.querySelector('#alcmeon-inmessage-toggle-button');
    if (button) {
      button.setAttribute('aria-label', this.translateService.instant('aria_label/open_alcmeon_inmessage'));
    }
  }

  /**
   * Injects the custom chat window css for each page
   * @param css The css string on the pageInfo object
   */
  private injectCSS(css: string) {
    const style = document.createElement('style');
    style.innerHTML = css;
    document.body.appendChild(style);
  }

  private injectLocalCSS(lookAndFeel) {
    this.http
      .get(`assets/look-and-feel/${lookAndFeel}/chat-window.css`, { responseType: 'text' })
      .subscribe(css => this.injectCSS(css));
  }
}
